import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { useToast } from '../../ui/use-toast';
import { climbsService } from '../../../services/climbs';
import type { Climb } from '../../../types/climb';
import type { RouteSetter } from '../../../types/route-setter';
import type { Zone } from '../../../types/zone';
import { ValidatedTitleInput } from '../../climb-create/ValidatedTitleInput';
import { ZonePicker } from '../../climb-create/ZonePicker';
import type { Step2ZoneSummary } from '../../climb-create/Step2Form';
import { RoutesetterPicker } from '../../routesetters/RoutesetterPicker';
import { CLIMB_EXCEPTIONS } from '../climb-exceptions';
import { ClimbSavedDialog } from './ClimbSavedDialog';
import { MIN_NAME_LENGTH } from './requirements';

/**
 * What the name field here needs to know: where the name has to be unique, and
 * which names are on offer. Absent where the climb was named before it was
 * saved — a competition problem, a board problem — because it is not asked
 * twice.
 */
export interface ClimbNaming {
  locationId: string | number;
  type: 'boulder' | 'route';
  suggestions?: { names: string[]; onManage: () => void };
  /** Strike a name off the queue once it has been given to a climb. */
  onUsed: (name: string) => void;
}

interface ClimbExtrasDialogProps {
  climb: Climb;
  locationSlug: string;
  zones: Zone[];
  /** Ask for a name here, and how. Left out, no name is asked for. */
  naming?: ClimbNaming | null;
  /**
   * What the climb already carries, when it did not start from nothing — a
   * scan's draft names its sector and its setter before the editor ever opens
   * one. Shown as the answers so far rather than as blanks, or saving this
   * dialog would quietly clear them.
   */
  initialZone?: Step2ZoneSummary | null;
  initialRouteSetter?: RouteSetter | null;
  /**
   * Set when the reader has no climb role at the place: the setter is then
   * picked the way a climber picks it, from the setters that exist or
   * themselves. See RoutesetterPicker.
   */
  memberUserId?: string | null;
  /** What the second button does, when it is not "set another one". */
  nextLabel?: string;
  /** Saved, and off to the climb's own page. */
  onDone: (climb: Climb) => void;
  /** Saved, and straight into the next climb on the same wall. */
  onNext: () => void;
}

/**
 * What is asked once the climb is already up: its name, the sector, the rules it
 * carries, who set it, and anything worth saying about it.
 *
 * None of it identifies the climb — not even the name, which almost nobody uses:
 * a problem is "the orange crimps under the roof", and the row in the list says
 * exactly that. A setter putting up a wall of problems in an afternoon meets
 * every one of these fields between one climb and the next, so the save happens
 * on the few things that are required and these come after — filled in now, or
 * left for whoever gets to it later.
 */
export function ClimbExtrasDialog({
  climb,
  locationSlug,
  zones,
  naming = null,
  initialZone = null,
  initialRouteSetter = null,
  memberUserId = null,
  nextLabel,
  onDone,
  onNext,
}: ClimbExtrasDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [name, setName] = useState(climb.name ?? '');
  const [nameTaken, setNameTaken] = useState(false);
  const [zone, setZone] = useState<Step2ZoneSummary | null>(initialZone);
  const [routeSetter, setRouteSetter] = useState<RouteSetter | null>(initialRouteSetter);
  const [exceptions, setExceptions] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // A name is optional here, but a half-typed one is not a name: rather than let
  // the write fail on the model's own rule, the buttons wait for it to be one.
  const typedName = name.trim();
  const nameUnusable =
    !!naming && ((typedName.length > 0 && typedName.length < MIN_NAME_LENGTH) || nameTaken);

  /**
   * Writes what was filled in, then hands over **the climb as it came back**: a
   * name given here re-slugs it, and handing on the copy this dialog opened with
   * would send the setter to the address it no longer has.
   *
   * A failure is reported and swallowed: the climb is up either way, and
   * refusing to move on would trap the setter in a dialog about fields none of
   * which were required.
   */
  const persist = async (then: (climb: Climb) => void) => {
    setSaving(true);
    try {
      const updated = await climbsService.updateClimb(
        climb.id,
        climb.type === 'Route' ? 'route' : 'boulder',
        {
          // Only where a name was asked for, and only as an answer: a dialog
          // that was shown no name field must not blank the one the climb has.
          ...(naming ? { name: typedName } : {}),
          zone_id: zone?.id ?? null,
          route_setter_id: routeSetter?.id ?? null,
          climb_exceptions: exceptions,
          description: description.trim() || null,
        }
      );
      // The name is spent: the next climb should not be offered the one this
      // took. A name typed by hand is not on the list and strikes nothing off.
      if (typedName) naming?.onUsed(typedName);
      then(updated);
      return updated;
    } catch (error) {
      toast({
        title: t('climbEditor.extras.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      // Nothing came back, so the climb is still what it was when it went up.
      then(climb);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ClimbSavedDialog
      open
      onOpenChange={(open) => !open && onDone(climb)}
      saving={saving}
      disabled={nameUnusable}
      onSave={() => void persist(onDone)}
      onSaveAndNext={() => void persist(onNext)}
      nextLabel={nextLabel ?? t('climbEditor.saved.another')}
    >
      {naming && (
        <div className="space-y-2">
          <Label htmlFor="title">{t('climbCreate.name')}</Label>
          {/* The compact field: the name being offered is behind the dice
              button inside the input — press it and a name off the list drops
              in, press it again for another — rather than sitting on a line of
              its own under the field. */}
          <ValidatedTitleInput
            title={name}
            locationId={naming.locationId}
            type={naming.type}
            // The climb this dialog is about already exists, so without this it
            // would report its own name back as taken.
            excludeClimbId={climb.id}
            onTitleChange={setName}
            onExistsChange={setNameTaken}
            compactSuggestions
            suggestions={naming.suggestions}
          />
        </div>
      )}

      {zones.length > 0 && (
        <div className="space-y-2">
          <Label>{t('climbCreate.zone')}</Label>
          <ZonePicker zones={zones} value={zone} onChange={setZone} />
        </div>
      )}

      <div className="space-y-2">
        <Label>{t('climb.route_setter')}</Label>
        <RoutesetterPicker
          mode="single"
          value={routeSetter ? [routeSetter] : []}
          onChange={(next) => setRouteSetter((next[0] as RouteSetter) ?? null)}
          locationId={locationSlug}
          placeholder={t('climb.route_setter_search_placeholder')}
          memberUserId={memberUserId}
        />
      </div>

      <div className="space-y-2">
        <Label>{t('climbCreate.climbExceptions')}</Label>
        <div className="flex flex-wrap gap-2">
          {CLIMB_EXCEPTIONS.map((exception) => {
            const on = exceptions.includes(exception);
            return (
              <button
                key={exception}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  setExceptions((current) =>
                    on ? current.filter((kept) => kept !== exception) : [...current, exception]
                  )
                }
                className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                  on
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input bg-background hover:bg-muted'
                }`}
              >
                {t(`climbExceptions.${exception}`)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="climb-extras-description">{t('climbPreview.description')}</Label>
        <Textarea
          id="climb-extras-description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={3}
          placeholder={t('climbPreview.description_placeholder')}
        />
      </div>
    </ClimbSavedDialog>
  );
}
